import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { Box, Divider, Grid } from "@mui/material";
import SocialMediaPages from "./SocialMediaPages";
import Information from "./Information";
import ContactUs from "./ContactUs";
import ShortVideo from "./ShortVideos";
import Policy from "../Policy/Policy";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLangPath } from "../../hooks/langAlias";

import { colors } from "../../theme/designTokens";
import { mergeManagedContent } from "../../hooks/mergeManagedContent";
import { PF } from "../../config";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

const trustBadges = [
  { icon: ShieldOutlinedIcon, label: "Licensed Pharmacy" },
  { icon: MedicationOutlinedIcon, label: "Genuine Medicines" },
  { icon: LocalShippingOutlinedIcon, label: "Fast Delivery" },
  { icon: AccessTimeOutlinedIcon, label: "24/7 Support" },
];

export default function Footer({
  policyOpen,
  setPolicyOpen,
  policyName,
  setPolicyName,
}) {
  const [innerPolicyDlg, setInnerPolicyDlg] = useState(false);
  const [innerPolicyName, setInnerPolicyName] = useState(false);
  const [contentSettings, setContentSettings] = useState([]);
  const [footerText, setFooterText] = useState("");
  const [langId, setLangId] = useState("");
  const navigate = useNavigate();
  const lp = useLangPath();

  // Store
  const { managedContentSettings } = useSelector(
    (state) => state.managedContentSettings
  );
  const { activeLangFront } = useSelector((state) => state.miscFront);
  const { webSettings } = useSelector((state) => state.webSettings);
  const { langs } = useSelector((state) => state.misc);

  const hasSocialLinks =
    (webSettings.socialMediaPages &&
      (webSettings.socialMediaPages.fb ||
        webSettings.socialMediaPages.twitter ||
        webSettings.socialMediaPages.linkedIn ||
        webSettings.socialMediaPages.telegram ||
        webSettings.socialMediaPages.instagram)) ||
    (webSettings.additionalSocialPages &&
      webSettings.additionalSocialPages.length > 0);

  // Set Language
  useEffect(() => {
    if (activeLangFront) setLangId(activeLangFront._id);
  }, [activeLangFront]);

  // Set Settings
  useEffect(() => {
    if (
      managedContentSettings &&
      managedContentSettings.length > 0 &&
      langId !== ""
    ) {
      const M = mergeManagedContent(managedContentSettings, langId, langs);
      if (M) {
        setContentSettings(M);
        if (M.footerText && M.siteSeoSettings && M.siteSeoSettings.length > 0) {
          let str = M.footerText;
          str = str.replace(
            /{_company_name}/g,
            M.siteSeoSettings[0].siteCompanyName
          );
          const year = new Date().getFullYear();
          str = str.replace(/{_year}/g, year);
          setFooterText(str);
        }
      }
    }
  }, [langId, managedContentSettings]);

  return (
    <>
      <Box sx={{ background: colors.paperTint }}>
        {/* Brand strip */}
        <Box sx={{ borderBottom: `1px solid ${colors.line}` }}>
          <Box
            sx={{
              maxWidth: 1400,
              mx: "auto",
              px: { xs: 2, md: 5 },
              py: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: 2.5,
            }}
          >
            <Box
              onClick={() => navigate(lp("/"))}
              sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}
            >
              <LazyLoadImage
                alt="Logo"
                effect="blur"
                src={
                  webSettings.siteLogo !== ""
                    ? PF + "/misc/" + webSettings.siteLogo
                    : "https://mfw.com.bd/wp-content/uploads/2021/05/getImage.png"
                }
                style={{ objectFit: "cover", maxHeight: 42 }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: 3.5,
                rowGap: 1.25,
              }}
            >
              {trustBadges.map((b, i) => (
                <Box
                  key={i}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      bgcolor: colors.white,
                      border: `1px solid ${colors.line}`,
                      color: colors.primary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <b.icon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: colors.ink2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {b.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Columns */}
        <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 5 } }}>
          <Grid container spacing={5} sx={{ py: { xs: 4, md: 5 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <Information
                setPolicyName={setInnerPolicyName}
                setPolicyOpen={setInnerPolicyDlg}
              />
            </Grid>
            {hasSocialLinks && (
              <Grid item xs={12} sm={6} md={3}>
                <SocialMediaPages />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <ContactUs contentSettings={contentSettings} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ShortVideo />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ borderColor: colors.line }} />
        <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 5 }, py: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.ink3,
              fontSize: 12.5,
            }}
          >
            {footerText}
          </Typography>
        </Box>
      </Box>
      {(policyOpen || innerPolicyDlg) && (
        <Policy
          policyOpen={policyOpen}
          setPolicyOpen={setPolicyOpen}
          policyName={policyName}
          contentSettings={contentSettings}
          innerPolicyDlg={innerPolicyDlg}
          setInnerPolicyDlg={setInnerPolicyDlg}
          innerPolicyName={innerPolicyName}
        />
      )}
    </>
  );
}
